// MyDesk — list of classes the student has been assigned to.
// Shows each class with its most recent published lesson and a "Open class" CTA.
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronRight, Library } from 'lucide-react';
import api from '../studentApi';
import { MyDeskClassesSkeleton } from '../StudentSkeletons';
import { ST_CARD_LINK, TINT_PRIMARY, StPageHeading, EmptyNote } from '../studentUi';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso.slice(0, 10); }
};

export default function MyDeskPage() {
  const { data, isPending: loading } = useQuery({
    queryKey: ['student', 'mydesk', 'overview'],
    queryFn: () => api.get('/student/mydesk/overview').catch(() => ({})),
  });
  const classes = data?.classes || [];
  const totalLessons = data?.total_lessons || 0;

  const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;
  return (
    <div className="space-y-8" data-testid="mydesk-page">
      <StPageHeading
        title="My classes"
        description={totalLessons > 0
          ? `${plural(totalLessons, 'lesson')} across ${plural(classes.length, 'class', 'classes')}, grouped by chapter`
          : 'Every lesson your teachers publish shows up here'} />

      {loading ? (
        <MyDeskClassesSkeleton />
      ) : classes.length === 0 ? (
        <EmptyNote testId="mydesk-empty">
          <Library className="w-5 h-5 mx-auto mb-3" />
          <span className="block font-medium" style={{ color: 'var(--st-ink)' }}>No classes yet</span>
          <span className="block mt-1">Once your school adds you to a class, its lessons appear here by chapter and date</span>
        </EmptyNote>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {classes.map(c => <ClassCard key={c.class_id} c={c} />)}
        </div>
      )}
    </div>
  );
}

function ClassCard({ c }) {
  const meta = [c.grade, c.subject_hint, `${c.lesson_count} lessons`].filter(Boolean).join(' · ');
  return (
    <Link to={`/student/mydesk/class/${c.class_id}`}
      data-testid={`mydesk-class-${c.class_id}`}
      className={`group ${ST_CARD_LINK} p-5 flex flex-col`}
      style={{ color: 'var(--st-ink)' }}>
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={TINT_PRIMARY}>
          <Library className="w-5 h-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold truncate">{c.class_name}</h3>
          <div className="text-sm mt-0.5 truncate" style={{ color: 'var(--st-mute)' }}>{meta}</div>
        </div>
        <ChevronRight className="w-4 h-4 shrink-0 mt-1 transition-[transform,color] group-hover:translate-x-0.5 text-[var(--st-mute)] group-hover:text-[var(--st-primary-ink)]" />
      </div>

      <div className="mt-4 pt-4 border-t text-sm" style={{ borderColor: 'var(--st-border)' }}>
        {c.next_up ? (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <span className="inline-flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--st-accent-ink)' }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--st-accent)' }} aria-hidden="true" />Latest
              </span>
              <span className="text-xs tabular-nums" style={{ color: 'var(--st-mute)' }}>{fmtDate(c.next_up.published_at)}</span>
            </div>
            <div className="font-medium mt-1 line-clamp-2">{c.next_up.title}</div>
            {c.next_up.summary && <p className="mt-1 line-clamp-2" style={{ color: 'var(--st-mute)' }}>{c.next_up.summary}</p>}
          </>
        ) : (
          <span style={{ color: 'var(--st-mute)' }}>No lessons yet</span>
        )}
      </div>
    </Link>
  );
}
